import { useState } from "react";
import type { MailDailyPoint } from "@feedback-hub/contract/browser";

import { axisTicks, dayTickLabel, problemBars, sendBars } from "./mailChart.js";
import { count } from "./mailFormat.js";

// One shared x-axis of N UTC days; two stacked panels in a single viewBox-scaled SVG.
const W = 720;
const LEFT = 40;
const RIGHT = 8;
const PANEL_H = 120;
const TOP_PAD = 14;
const GAP = 34; // room between panels for the sends panel's x labels
const PLOT_W = W - LEFT - RIGHT;
const SEND_TOP = TOP_PAD;
const PROB_TOP = TOP_PAD + PANEL_H + GAP;
const H = PROB_TOP + PANEL_H + 22;

function plural(n: number, word: string): string {
  return `${count(n)} ${word}${n === 1 ? "" : "s"}`;
}

export function readout(p: MailDailyPoint): string {
  const parts = [`${count(p.sends)} recorded`];
  if (p.unattributed != null) parts.push(`${count(p.unattributed)} not recorded`);
  parts.push(plural(p.bounces, "bounce"), plural(p.complaints, "complaint"));
  return `${dayTickLabel(p.day)}: ${parts.join(", ")}`;
}

export function MailChart({ points }: { points: MailDailyPoint[] }) {
  const [active, setActive] = useState<number | null>(null);
  const n = points.length;
  if (n === 0) return <p className="muted">No days to chart.</p>;

  const colW = PLOT_W / n;
  const barW = Math.max(1, colW - 2); // 2px gap between adjacent bars
  const sends = sendBars(points, PANEL_H);
  const problems = problemBars(points, PANEL_H);
  const showUnattributed = points.some((p) => p.unattributed != null);
  const colX = (i: number) => LEFT + i * colW;

  const panel = (top: number, ceiling: number) =>
    axisTicks(ceiling).map((t) => {
      const y = top + PANEL_H - (t / ceiling) * PANEL_H;
      return (
        <g key={t}>
          <line className="chart-grid" x1={LEFT} x2={W - RIGHT} y1={y} y2={y} />
          <text className="chart-axis" x={LEFT - 6} y={y + 3} textAnchor="end">
            {t}
          </text>
        </g>
      );
    });

  const xLabels = (top: number) =>
    points.map((p, i) =>
      i % 5 === 0 ? (
        <text
          key={p.day}
          className="chart-axis"
          x={colX(i) + colW / 2}
          y={top + PANEL_H + 14}
          textAnchor="middle"
        >
          {dayTickLabel(p.day)}
        </text>
      ) : null,
    );

  const activePoint = active != null ? points[active] : undefined;
  const hitH = PROB_TOP + PANEL_H - SEND_TOP;

  return (
    <figure className="mail-chart">
      <div className="chart-readout">
        {activePoint ? readout(activePoint) : "Hover or focus a day for details."}
      </div>
      <ul className="chart-legend">
        <li>
          <span className="swatch swatch-recorded" aria-hidden="true" /> Recorded by the gateway
        </li>
        {showUnattributed && (
          <li>
            <span className="swatch swatch-unattributed" aria-hidden="true" /> Not recorded by the gateway
          </li>
        )}
        <li>
          <span className="swatch swatch-bounce" aria-hidden="true" /> Bounces
        </li>
        <li>
          <span className="swatch swatch-complaint" aria-hidden="true" /> Complaints
        </li>
      </ul>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="group"
        aria-label="Daily sends, bounces and complaints"
        onMouseLeave={() => setActive(null)}
      >
        <defs>
          <pattern
            id="mail-hatch"
            patternUnits="userSpaceOnUse"
            width="5"
            height="5"
            patternTransform="rotate(45)"
          >
            <rect width="5" height="5" className="hatch-bg" />
            <line x1="0" y1="0" x2="0" y2="5" className="hatch-line" strokeWidth="2" />
          </pattern>
        </defs>

        <text className="chart-title" x={LEFT} y={9}>
          Emails sent per day
        </text>
        {panel(SEND_TOP, sends.ceiling)}
        {xLabels(SEND_TOP)}
        {sends.bars.map((b, i) => (
          <g key={b.day}>
            <rect
              className="bar-recorded"
              x={colX(i) + 1}
              y={SEND_TOP + PANEL_H - b.recordedH}
              width={barW}
              height={b.recordedH}
            />
            {b.unattributedH > 0 && (
              <rect
                className="bar-unattributed"
                x={colX(i) + 1}
                y={SEND_TOP + PANEL_H - b.recordedH - b.unattributedH}
                width={barW}
                height={b.unattributedH}
              />
            )}
          </g>
        ))}

        <text className="chart-title" x={LEFT} y={PROB_TOP - 5}>
          Bounces and complaints per day
        </text>
        {panel(PROB_TOP, problems.ceiling)}
        {xLabels(PROB_TOP)}
        {problems.bars.map((b, i) => {
          const half = Math.max(1, (barW - 2) / 2);
          return (
            <g key={b.day}>
              <rect
                className="bar-bounce"
                x={colX(i) + 1}
                y={PROB_TOP + PANEL_H - b.bouncesH}
                width={half}
                height={b.bouncesH}
              />
              <rect
                className="bar-complaint"
                x={colX(i) + 1 + half + 2}
                y={PROB_TOP + PANEL_H - b.complaintsH}
                width={half}
                height={b.complaintsH}
              />
            </g>
          );
        })}

        {points.map((p, i) => (
          <g key={`hit-${p.day}`}>
            {active === i && (
              <rect className="chart-active" x={colX(i)} y={SEND_TOP} width={colW} height={hitH} />
            )}
            <rect
              className="chart-hit"
              x={colX(i)}
              y={SEND_TOP}
              width={colW}
              height={hitH}
              tabIndex={0}
              role="img"
              aria-label={readout(p)}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive((cur) => (cur === i ? null : cur))}
            />
          </g>
        ))}
      </svg>

      <figcaption className="muted chart-caption">
        Days are UTC. Bounces and complaints are counted on the day they were reported.
      </figcaption>

      <details>
        <summary>Show daily numbers</summary>
        <div className="table-wrap">
          <table className="queue">
            <thead>
              <tr>
                <th>Day (UTC)</th>
                <th>Sent</th>
                <th>Delivered</th>
                <th>Bounced</th>
                <th>Complained</th>
                {showUnattributed && <th>SES sends</th>}
                {showUnattributed && <th>Not recorded</th>}
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.day}>
                  <td>{p.day}</td>
                  <td>{count(p.sends)}</td>
                  <td>{count(p.deliveries)}</td>
                  <td>{count(p.bounces)}</td>
                  <td>{count(p.complaints)}</td>
                  {showUnattributed && <td>{p.sesSends == null ? "-" : count(p.sesSends)}</td>}
                  {showUnattributed && <td>{p.unattributed == null ? "-" : count(p.unattributed)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
